/*
 * Cottomix theme — header behaviour (cm-header.js)
 *
 * Purpose: all interactive behaviour of the sticky header, owned by the single
 * custom element <cm-header> rendered by sections/header.liquid:
 *   A) desktop navigation — hover-intent (grace delay from
 *      --cm-motion-mega-menu-delay, fallback 150 ms), click toggle, keyboard
 *      (ArrowDown / Escape / focus-out), outside-click and scroll dismissal,
 *      panel-open marker on .section-header;
 *   B) mobile menu drawer — nested sliding panels ([data-cm-menu-panel]) with
 *      a path stack, back buttons, reset on drawer close, auto-close on
 *      resize to >= 1024 px;
 *   C) language switcher — exclusive <details data-cm-lang> handling;
 *   D) cart — link opens the cart drawer when present, count bubbles follow
 *      CM 'cart:updated';
 *   E) store-locator triggers — hidden when the overlay does not exist;
 *   F) location chip — reflects the persisted branch (localStorage "cm:store")
 *      and opens the store-locator overlay.
 *
 * Hooks consumed (inside <cm-header>):
 *   [data-cm-nav-item] (li) > [data-cm-nav-toggle] (button) + [data-cm-nav-panel]
 *   #menu-drawer > [data-cm-menu-stack] > [data-cm-menu-panel="ID"]
 *     with [data-cm-menu-open="PANEL_ID"] openers and [data-cm-menu-back] buttons
 *   details[data-cm-lang] (language switcher)
 *   [data-cm-cart-link] / [data-cm-cart-count]
 *   [data-cm-open="store-locator"]
 *   .section-header wrapper gets data-cm-panel-open while a panel is open
 *
 * Events emitted:
 *   document 'cm-header:panel-open'       — a desktop nav panel was opened
 *   CM 'cart:open' { source: 'header' }   — cart link activated, drawer exists
 *
 * Events consumed:
 *   document 'cm-search:open' — a search panel opened: close all nav panels
 *   document 'cm-store-changed' — the picked branch changed: refresh the chip
 *   CM 'drawer:close'         — reset the drawer menu path (menu-drawer only)
 *   CM 'cart:updated'         — re-render the cart count bubbles
 *   CM 'store:changed'        — the picked branch changed: refresh the chip
 *   CM 'ready'                — re-check store-locator triggers
 *
 * Global API used (every use guarded with window.CM &&): CM.on / CM.emit /
 * CM.close / CM.config.routes. Vanilla ES2017, no dependencies.
 */
(function () {
  'use strict';

  function readMs(value, fallback) {
    var raw = String(value == null ? '' : value).trim();
    var match = raw.match(/^([\d.]+)\s*(ms|s)?$/);
    if (!match) return fallback;
    var num = parseFloat(match[1]);
    if (isNaN(num)) return fallback;
    return match[2] === 's' ? num * 1000 : num;
  }

  class CMHeader extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const bound = [];
      const on = (target, type, fn, opts) => {
        target.addEventListener(type, fn, opts);
        bound.push({ target: target, type: type, fn: fn, opts: opts });
      };
      const timers = [];
      const later = (fn, ms) => {
        const id = setTimeout(fn, ms);
        timers.push(id);
        return id;
      };
      const unsubs = [];
      let resizeTimer = null;

      /* ---------------- A) desktop navigation ---------------- */

      const items = Array.prototype.slice.call(
        this.querySelectorAll('[data-cm-nav-item]')
      ).filter((item) => item.querySelector('[data-cm-nav-panel]'));

      const graceMs = readMs(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--cm-motion-mega-menu-delay'),
        150
      );

      const clearCloseTimer = (item) => {
        if (item._t) {
          clearTimeout(item._t);
          item._t = null;
        }
      };

      const setPanelMarker = (open) => {
        const header = this.closest('.section-header');
        if (!header) return;
        if (open) header.setAttribute('data-cm-panel-open', '');
      };

      const updatePanelMarker = () => {
        const header = this.closest('.section-header');
        if (!header) return;
        const anyOpen =
          this.querySelector('[data-cm-nav-item][data-open]') ||
          this.querySelector('cm-predictive-search[data-open]');
        if (anyOpen) header.setAttribute('data-cm-panel-open', '');
        else header.removeAttribute('data-cm-panel-open');
      };

      const closeItem = (item) => {
        item.removeAttribute('data-open');
        const toggle = item.querySelector('[data-cm-nav-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        updatePanelMarker();
      };

      const closeAll = (except) => {
        items.forEach((item) => {
          if (item !== except) closeItem(item);
        });
      };

      const openItem = (item) => {
        closeAll(item);
        clearCloseTimer(item);
        item.setAttribute('data-open', '');
        const toggle = item.querySelector('[data-cm-nav-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
        setPanelMarker(true);
        document.dispatchEvent(new CustomEvent('cm-header:panel-open'));
      };

      const scheduleClose = (item) => {
        clearCloseTimer(item);
        item._t = setTimeout(() => {
          item._t = null;
          closeItem(item);
        }, graceMs);
      };

      const focusFirstPanelLink = (item) => {
        requestAnimationFrame(() => {
          const first = item.querySelector('[data-cm-nav-panel] a');
          if (first) first.focus();
        });
      };

      items.forEach((item) => {
        on(item, 'pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearCloseTimer(item);
          openItem(item);
        });
        on(item, 'pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          scheduleClose(item);
        });
        on(item, 'focusout', (event) => {
          if (!item.contains(event.relatedTarget)) closeItem(item);
        });
        on(item, 'keydown', (event) => {
          const target = event.target;
          const onLinkOrToggle =
            target &&
            target.closest &&
            (target.closest('a') || target.closest('[data-cm-nav-toggle]'));
          if (event.key === 'ArrowDown' && onLinkOrToggle) {
            event.preventDefault();
            openItem(item);
            focusFirstPanelLink(item);
          } else if (event.key === 'Escape' && item.hasAttribute('data-open')) {
            closeItem(item);
            const toggle = item.querySelector('[data-cm-nav-toggle]');
            if (toggle) toggle.focus();
          }
        });
        const toggle = item.querySelector('[data-cm-nav-toggle]');
        if (toggle) {
          on(toggle, 'click', () => {
            if (item.hasAttribute('data-open')) closeItem(item);
            else openItem(item);
          });
        }
      });

      on(document, 'pointerdown', (event) => {
        const target = event.target;
        if (
          !this.querySelector('[data-cm-nav-item][data-open]') ||
          (target && target.closest && target.closest('[data-cm-nav-item][data-open]'))
        ) {
          return;
        }
        closeAll();
      });

      const onHeaderScroll = () => {
        if (this.querySelector('[data-cm-nav-item][data-open]')) closeAll();
      };
      on(window, 'scroll', onHeaderScroll, { passive: true });

      on(document, 'cm-search:open', () => {
        closeAll();
      });

      /* ---------------- A3) nav accent items (G017) ----------------
         Jlood xHighlightText: nav items in the viewport get .is-active at
         init (nav marks carry no delay attr); one tick after the first paint
         so the 700ms colour/clip-path sweep is visible. */
      const hlItems = this.querySelectorAll('[data-cm-hl]');
      if (hlItems.length) {
        later(() => {
          Array.prototype.forEach.call(hlItems, (el) => {
            el.classList.add('is-active');
          });
        }, 0);
      }

      /* ---------------- A2) nav row ghost scroll arrows ----------------
         Jlood xScrollMenu .scroll-nav: prev/next buttons exist only while
         .cm-nav__list overflows, scrollBy 200px (smooth; auto under reduced
         motion), disabled with opacity .4 at the edges, RTL-aware. */

      const nav = this.querySelector('.cm-nav');
      const navList = nav && nav.querySelector('.cm-nav__list');
      let navArrows = null;

      const navScrollBy = (delta) => {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        navList.scrollBy({ left: delta, behavior: reduce ? 'auto' : 'smooth' });
      };

      const updateNavArrows = () => {
        if (!navArrows) return;
        const max = navList.scrollWidth - navList.clientWidth;
        const scrollable = max > 1;
        navArrows.prev.hidden = !scrollable;
        navArrows.next.hidden = !scrollable;
        if (!scrollable) return;
        // scrollLeft is negative toward the inline end in modern RTL, so the
        // absolute value detects both edges in every direction model.
        const pos = Math.abs(navList.scrollLeft);
        navArrows.prev.disabled = pos < 1;
        navArrows.next.disabled = pos >= max - 1;
      };

      if (navList) {
        const rtl = () => getComputedStyle(navList).direction === 'rtl';
        const makeArrow = (mod, label) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cm-nav__arrow cm-nav__arrow--' + mod;
          btn.setAttribute('aria-label', label);
          btn.innerHTML =
            '<svg class="cm-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6"/></svg>';
          btn.addEventListener('click', () => {
            const dir = mod === 'prev' ? -1 : 1;
            navScrollBy(dir * (rtl() ? -200 : 200));
          });
          nav.appendChild(btn);
          return btn;
        };
        navArrows = {
          prev: makeArrow('prev', nav.getAttribute('data-cm-nav-prev-label') || ''),
          next: makeArrow('next', nav.getAttribute('data-cm-nav-next-label') || '')
        };

        on(navList, 'scroll', updateNavArrows, { passive: true });
        if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
          document.fonts.ready.then(updateNavArrows);
        }
        // initial check after layout (defer script + web fonts can shift widths)
        later(updateNavArrows, 0);
        later(updateNavArrows, 300);
      }

      /* ---------------- B) mobile drawer nested panels ---------------- */

      const drawer = this.querySelector('#menu-drawer');
      const stack = drawer && drawer.querySelector('[data-cm-menu-stack]');
      if (stack) {
        let path = ['cm-menu-panel-root'];
        const openers = {};
        const panels = Array.prototype.slice.call(
          stack.querySelectorAll('[data-cm-menu-panel]')
        );

        const apply = () => {
          panels.forEach((panel) => {
            const id = panel.dataset.cmMenuPanel;
            const active = id === path[path.length - 1];
            const before = !active && path.indexOf(id) !== -1;
            panel.classList.toggle('is-active', active);
            panel.classList.toggle('is-before', before);
            panel.inert = !active;
            panel.setAttribute('aria-hidden', String(!active));
          });
        };

        const onStackClick = (event) => {
          const target = event.target;
          if (!target || !target.closest) return;
          const opener = target.closest('[data-cm-menu-open]');
          if (opener && stack.contains(opener)) {
            const id = opener.getAttribute('data-cm-menu-open');
            path.push(id);
            openers[id] = opener;
            opener.setAttribute('aria-expanded', 'true');
            apply();
            requestAnimationFrame(() => {
              const panel = stack.querySelector(
                '[data-cm-menu-panel="' + id + '"]'
              );
              const back = panel && panel.querySelector('[data-cm-menu-back]');
              if (back) back.focus();
            });
            return;
          }
          const back = target.closest('[data-cm-menu-back]');
          if (back && stack.contains(back)) {
            const id = path.pop();
            apply();
            const opener2 = openers[id];
            if (opener2) {
              opener2.setAttribute('aria-expanded', 'false');
              opener2.focus();
            }
          }
        };
        on(stack, 'click', onStackClick);

        // G018: the desktop catalog button pushes the catalog view onto the
        // same path stack (Jlood DrawerMenu); drawer:close resets to root.
        Array.prototype.forEach.call(
          this.querySelectorAll('[data-cm-menu-view]'),
          (btn) => {
            on(btn, 'click', () => {
              const id = btn.getAttribute('data-cm-menu-view');
              if (!id || path.indexOf(id) !== -1) return;
              path.push(id);
              openers[id] = btn;
              apply();
              requestAnimationFrame(() => {
                const panel = stack.querySelector(
                  '[data-cm-menu-panel="' + id + '"]'
                );
                const back = panel && panel.querySelector('[data-cm-menu-back]');
                if (back) back.focus();
              });
            });
          }
        );

        const reset = () => {
          path = ['cm-menu-panel-root'];
          Object.keys(openers).forEach((id) => {
            openers[id].setAttribute('aria-expanded', 'false');
          });
          apply();
        };

        if (window.CM && CM.on) {
          unsubs.push(
            CM.on('drawer:close', (d) => {
              if (d && d.id === 'menu-drawer') later(reset, 350);
            })
          );
        }

        const onResize = () => {
          if (
            window.innerWidth >= 1024 &&
            drawer.hasAttribute('open') &&
            window.CM &&
            CM.close
          ) {
            CM.close('menu-drawer');
          }
        };
        on(window, 'resize', () => {
          if (resizeTimer) clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            resizeTimer = null;
            onResize();
          }, 150);
        });
        apply();
      }

      /* ---------------- C) language switcher ---------------- */

      const getLangs = () =>
        Array.prototype.slice.call(document.querySelectorAll('details[data-cm-lang]'));

      const onLangPointerDown = (event) => {
        getLangs().forEach((details) => {
          if (
            details.hasAttribute('open') &&
            !details.contains(event.target)
          ) {
            details.removeAttribute('open');
          }
        });
      };
      on(document, 'pointerdown', onLangPointerDown);

      const onLangKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        const open = getLangs().filter((d) => d.hasAttribute('open'))[0];
        if (!open) return;
        event.stopPropagation();
        open.removeAttribute('open');
        const summary = open.querySelector('summary');
        if (summary) summary.focus();
      };
      on(document, 'keydown', onLangKeyDown, true);

      getLangs().forEach((details) => {
        on(details, 'toggle', () => {
          if (!details.hasAttribute('open')) return;
          getLangs().forEach((other) => {
            if (other !== details) other.removeAttribute('open');
          });
        });
      });

      /* ---------------- D) cart link + count ---------------- */

      const onCartClick = (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const link = target.closest('[data-cm-cart-link]');
        if (!link) return;
        if (
          event.defaultPrevented ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.button !== 0
        ) {
          return;
        }
        const cartUrl =
          (window.CM && CM.config && CM.config.routes && CM.config.routes.cart_url) ||
          '/cart';
        if (location.pathname === cartUrl) return;
        const cartDrawer = document.getElementById('cart-drawer');
        if (!cartDrawer) return;
        event.preventDefault();
        if (window.CM && CM.emit) CM.emit('cart:open', { source: 'header' });
        if (
          typeof cartDrawer.open === 'function' &&
          !cartDrawer.hasAttribute('open')
        ) {
          cartDrawer.open();
        }
      };
      on(document, 'click', onCartClick);

      if (window.CM && CM.on) {
        const setCount = (n) => {
          const count = Number(n) || 0;
          Array.prototype.forEach.call(document.querySelectorAll('[data-cm-cart-count]'), (el) => {
            el.textContent = count > 99 ? '99+' : String(count);
            el.hidden = count < 1;
          });
        };
        let cartCheck = null;
        const askCart = () => {
          clearTimeout(cartCheck);
          cartCheck = setTimeout(() => {
            if (window.CM && CM.cart && CM.cart.get) CM.cart.get().catch(() => {});
          }, 600);
        };
        timers.push({ clear: () => clearTimeout(cartCheck) });
        unsubs.push(CM.on('cart:get', (cart) => {
          clearTimeout(cartCheck);
          if (cart && typeof cart.item_count === 'number') setCount(cart.item_count);
        }));
        unsubs.push(CM.on('cart:updated', (detail) => {
          const cart = detail && detail.cart;
          if (cart && typeof cart.item_count === 'number') setCount(cart.item_count); else askCart();
        }));
      }

      /* ---------------- E) store-locator triggers ---------------- */

      const hideStoreLocator = () => {
        if (document.getElementById('store-locator')) return;
        Array.prototype.forEach.call(
          this.querySelectorAll('[data-cm-open="store-locator"]'),
          (el) => {
            el.hidden = true;
          }
        );
      };
      hideStoreLocator();
      if (window.CM && CM.on) {
        unsubs.push(CM.on('ready', hideStoreLocator));
      }

      /* ---------------- E2) search overlay (b18 G1) ----------------
         The header search icon opens the #search-overlay modal via the
         generic data-cm-open handler (theme.js). When it opens, focus the
         input and show the recent/popular panel so the shopper can start
         typing immediately. */
      if (window.CM && CM.on) {
        unsubs.push(
          CM.on('modal:open', (d) => {
            if (!d || d.id !== 'search-overlay') return;
            const overlay = document.getElementById('search-overlay');
            if (!overlay) return;
            const input = overlay.querySelector('.cm-search__input');
            const search = overlay.querySelector('cm-predictive-search');
            requestAnimationFrame(() => {
              if (input) input.focus();
              if (search && typeof search.openPanel === 'function') search.openPanel();
            });
          })
        );
      }

      /* ---------------- F) location chip (persisted branch) ---------------- */

      // Jlood keeps the picked store in localStorage and shows a chip in the
      // header actions; the chip opens the same store-locator overlay.
      const chip = this.querySelector('[data-cm-store-chip]');
      if (chip) {
        const chipLabel = chip.querySelector('[data-cm-store-chip-label]');
        const STORE_KEY = 'cm:store';

        const readStore = () => {
          try {
            return JSON.parse(window.localStorage.getItem(STORE_KEY));
          } catch (err) {
            return null;
          }
        };

        const updateChip = () => {
          const stored = readStore();
          if (stored && stored.name && chipLabel) {
            chipLabel.textContent = stored.name;
            chip.hidden = false;
          } else {
            chip.hidden = true;
          }
        };
        updateChip();

        const onStoreChanged = () => updateChip();
        if (window.CM && CM.on) {
          unsubs.push(CM.on('store:changed', onStoreChanged));
        }
        on(document, 'cm-store-changed', onStoreChanged);
      }

      /* ---------------- teardown ---------------- */

      this._teardown = () => {
        bound.forEach((entry) => {
          entry.target.removeEventListener(
            entry.type,
            entry.fn,
            entry.opts
          );
        });
        timers.forEach((id) => { if (id && typeof id.clear === 'function') id.clear(); else clearTimeout(id); });
        unsubs.forEach((unsub) => {
          if (typeof unsub === 'function') unsub();
        });
        items.forEach(clearCloseTimer);
        if (resizeTimer) clearTimeout(resizeTimer);
      };
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      if (typeof this._teardown === 'function') this._teardown();
      this._teardown = null;
    }
  }

  if (!customElements.get('cm-header')) {
    customElements.define('cm-header', CMHeader);
  }
})();
